import { io, type Socket } from 'socket.io-client';
import { ApiError } from './api';
import { SESSION_PROTOCOL_VERSION } from './session-core';
import { trackAnalytics } from './analytics';

export type PairInteraction = 'same_brain' | 'read_me' | 'dont_match';
export type MatchmakingStatus = 'idle' | 'waiting' | 'matched' | 'cancelled' | 'expired';
export interface MatchmakingSnapshot {
  protocolVersion: 1;
  matchmaking: {
    status: MatchmakingStatus; interactionType: PairInteraction | null; revision: number;
    createdAt: string | null; updatedAt: string | null; expiresAt: string | null;
  };
  match: null | { sessionId: string; person: { firstName: string } };
}

const activeKey = 'utage.matchmakingActive';
export const matchmakingIsActive = (): boolean => localStorage.getItem(activeKey) === '1';
export const rememberMatchmakingActive = (active: boolean): void => {
  if (active) localStorage.setItem(activeKey, '1'); else localStorage.removeItem(activeKey);
};

const request = async (path: string, initData: string, options?: RequestInit): Promise<MatchmakingSnapshot> => {
  const response = await fetch(path, {
    ...options, cache: 'no-store',
    headers: { Accept: 'application/json', Authorization: `tma ${initData}`, 'Content-Type': 'application/json', 'X-UTAGE-Protocol': String(SESSION_PROTOCOL_VERSION), ...options?.headers }
  });
  const body = await response.json().catch(() => ({})) as MatchmakingSnapshot & { error?: { code?: string; message?: string } };
  if (!response.ok) throw new ApiError(body.error?.message ?? 'Could not update matchmaking.', body.error?.code);
  return body;
};

export const loadMatchmaking = (initData: string): Promise<MatchmakingSnapshot> => request('/matchmaking/state', initData);
export const findSomeone = (initData: string, interactionType: PairInteraction): Promise<MatchmakingSnapshot> =>
  request('/matchmaking/find', initData, { method: 'POST', body: JSON.stringify({ actionId: crypto.randomUUID(), interactionType }) });
export const cancelSomeoneNew = (initData: string): Promise<MatchmakingSnapshot> =>
  request('/matchmaking/cancel', initData, { method: 'POST', body: JSON.stringify({ actionId: crypto.randomUUID() }) });

export const watchMatchmaking = (input: {
  initData: string; onSnapshot: (snapshot: MatchmakingSnapshot) => void; onConnection: (connected: boolean) => void;
}): (() => void) => {
  const socket: Socket = io({ auth: { initData: input.initData, protocolVersion: SESSION_PROTOCOL_VERSION }, reconnection: true });
  let disconnected = false;
  let disposed = false;
  socket.on('connect', () => {
    input.onConnection(true);
    if (disconnected) trackAnalytics('recovery_attempted', { scope: 'matchmaking', mechanism: 'socket_resume' });
    socket.emit('matchmaking:watch', {}, (result: { ok: boolean; snapshot?: MatchmakingSnapshot }) => {
      if (result.ok && result.snapshot) {
        input.onSnapshot(result.snapshot);
        if (disconnected) trackAnalytics('recovery_succeeded', { scope: 'matchmaking', mechanism: 'socket_resume' });
        disconnected = false;
      }
    });
  });
  socket.on('disconnect', () => { if (!disposed) { disconnected = true; trackAnalytics('socket_disconnected', { scope: 'matchmaking' }); input.onConnection(false); } });
  socket.on('matchmaking:changed', () => {
    void loadMatchmaking(input.initData).then(input.onSnapshot).catch(() => undefined);
  });
  const restore = (): void => {
    if (document.visibilityState !== 'visible') return;
    trackAnalytics('recovery_attempted', { scope: 'matchmaking', mechanism: 'snapshot' });
    void loadMatchmaking(input.initData).then((snapshot) => {
      input.onSnapshot(snapshot);
      trackAnalytics('snapshot_recovery_used', { scope: 'matchmaking', mechanism: 'visibility' });
      trackAnalytics('recovery_succeeded', { scope: 'matchmaking', mechanism: 'snapshot' });
    }).catch(() => trackAnalytics('recovery_failed', { scope: 'matchmaking', mechanism: 'snapshot' }));
  };
  document.addEventListener('visibilitychange', restore);
  return () => { disposed = true; socket.disconnect(); document.removeEventListener('visibilitychange', restore); };
};
